import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  Clock, AlertTriangle, DollarSign, FileText, RefreshCw,
  TrendingUp, Users, ChevronDown, ChevronUp,
} from 'lucide-react';
import { FilterPanel } from '@/components/FilterPanel';
import { HelpBubble } from '@/components/HelpBubble';
import { ExportDropdown } from '@/components/ExportDropdown';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import { useGlobalFilters } from '@/lib/FilterContext';
import { useCurrency } from '@/lib/useCurrency';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { toast } from 'sonner';

const BUCKET_COLORS = ['#22c55e', '#f59e0b', '#f97316', '#ef4444', '#dc2626'];

function fmt(v) {
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v?.toFixed(0) || '0';
}

export default function InvoiceAging() {
  const { symbol: cs } = useCurrency();
  const { filters, setFilters } = useGlobalFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sortField, setSortField] = useState('days_overdue');
  const [sortDir, setSortDir] = useState('desc');

  const buildQueryString = useCallback((f) => {
    const params = new URLSearchParams();
    if (f.category) params.append('category', f.category);
    if (f.supplier) params.append('supplier', f.supplier);
    if (f.region) params.append('region', f.region);
    if (f.sbu) params.append('sbu', f.sbu);
    if (f.fiscal_year) params.append('fiscal_year', f.fiscal_year);
    if (f.cost_center) params.append('cost_center', f.cost_center);
    if (f.business_unit) params.append('business_unit', f.business_unit);
    if (f.addressability) params.append('addressability', f.addressability);
    return params.toString();
  }, []);

  const fetchData = useCallback(async (f = filters) => {
    try {
      const qs = buildQueryString(f);
      const res = await api.get(`/analytics/invoice-aging${qs ? `?${qs}` : ''}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load invoice aging data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildQueryString, filters]);

  useEffect(() => { fetchData(); }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setRefreshing(true);
    fetchData(newFilters);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(filters);
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedDetails = [...(data?.details || [])].sort((a, b) => {
    const aVal = a[sortField] ?? 0;
    const bVal = b[sortField] ?? 0;
    if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const buckets = data?.buckets || [];
  const overdue = data?.overdue_suppliers || [];
  const catAging = data?.category_aging || [];
  const activeFiltersCount = Object.values(filters).filter(v => v !== null && v !== '' && v !== 'all').length;

  const pieData = buckets.filter(b => b.amount > 0).map((b, i) => ({
    name: b.bucket, value: b.amount, fill: BUCKET_COLORS[i] || '#94a3b8',
  }));

  const exportCSV = () => {
    const rows = [
      ['--- KPIs ---'],
      ['Total Invoices', kpis.total_invoices],
      ['Total Spend', kpis.total_spend],
      ['Outstanding', kpis.total_outstanding],
      ['Overdue', kpis.total_overdue],
      ['Overdue %', `${kpis.overdue_percentage}%`],
      [],
      ['--- Aging Buckets ---'],
      ['Bucket', 'Count', 'Amount'],
      ...buckets.map(b => [b.bucket, b.count, b.amount]),
      [],
      ['--- Overdue Suppliers ---'],
      ['Supplier', 'Overdue Amount', 'Invoices', 'Oldest Overdue Days', 'Category'],
      ...overdue.map(s => [s.supplier, s.overdue_amount, s.invoice_count, s.oldest_overdue_days, s.category]),
      [],
      ['--- Details ---'],
      ['Supplier', 'Invoice #', 'Invoice Date', 'Due Date', 'Amount', 'Days Overdue', 'Bucket', 'Category'],
      ...sortedDetails.map(d => [d.supplier_name, d.invoice_number, d.invoice_date, d.due_date, d.amount, d.days_overdue, d.bucket, d.category]),
    ];
    downloadCSV(rows, 'invoice_aging');
  };

  const exportPDF = () => {
    const sections = [
      { title: 'KPIs', headers: ['Metric', 'Value'], rows: [
        ['Total Invoices', String(kpis.total_invoices)],
        ['Total Spend', `${cs}${kpis.total_spend?.toLocaleString()}`],
        ['Overdue Amount', `${cs}${kpis.total_overdue?.toLocaleString()}`],
        ['Overdue %', `${kpis.overdue_percentage}%`],
      ]},
      { title: 'Aging Buckets', headers: ['Bucket', 'Count', 'Amount'], rows: buckets.map(b => [b.bucket, String(b.count), `${cs}${b.amount?.toLocaleString()}`]) },
      { title: 'Top Overdue Suppliers', headers: ['Supplier', 'Overdue', 'Invoices', 'Oldest Days'], rows: overdue.slice(0, 15).map(s => [s.supplier, `${cs}${s.overdue_amount?.toLocaleString()}`, String(s.invoice_count), String(s.oldest_overdue_days)]) },
    ];
    buildPDF(sections, 'Invoice_Aging');
  };

  const SortHeader = ({ field, children }) => (
    <th
      className="text-left py-2 px-3 font-medium cursor-pointer hover:text-primary transition-colors select-none"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
      </span>
    </th>
  );

  return (
    <div className="space-y-4" data-testid="invoice-aging-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Invoice Aging Analysis</h1>
          <p className="text-muted-foreground mt-1">
            Track overdue invoices, aging buckets, and supplier payment health
            {activeFiltersCount > 0 && (
              <span className="ml-2 text-primary font-medium">({activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-aging">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        pageName="invoice-aging"
        visibleFilters={['category', 'supplier', 'region', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
        compact={true}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <Card data-testid="kpi-total-invoices">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> Total Invoices <HelpBubble text="Total number of invoice rows in the filtered view. Only invoices with a due-date signal are eligible for aging calculations." /></p>
              <p className="text-xl font-bold font-mono">{kpis.total_invoices?.toLocaleString()}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card data-testid="kpi-total-spend">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="w-3 h-3" /> Total Spend <HelpBubble text="Total invoice value across the filtered view, FX-normalised." /></p>
              <p className="text-xl font-bold font-mono">{cs}{fmt(kpis.total_spend || 0)}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card data-testid="kpi-outstanding">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Outstanding <HelpBubble text="Value of invoices that have not yet been paid — includes both not-yet-due and overdue amounts." /></p>
              <p className="text-xl font-bold font-mono">{cs}{fmt(kpis.total_outstanding || 0)}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="border-l-4 border-l-red-500" data-testid="kpi-overdue">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Overdue <HelpBubble text="Portion of Outstanding that is past its due date (invoice_date + payment_terms). Focus early recovery efforts here." /></p>
              <p className="text-xl font-bold font-mono text-red-500">{cs}{fmt(kpis.total_overdue || 0)}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card data-testid="kpi-overdue-pct">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Overdue % <HelpBubble text="Overdue amount as a share of Total Spend. Colour: green <10%, amber 10-20%, red >20%. Benchmark against your industry norm." /></p>
              <p className={`text-xl font-bold font-mono ${kpis.overdue_percentage > 20 ? 'text-red-500' : kpis.overdue_percentage > 10 ? 'text-amber-500' : 'text-emerald-500'}`}>
                {kpis.overdue_percentage}%
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Aging Overview</TabsTrigger>
          <TabsTrigger value="suppliers">Overdue Suppliers</TabsTrigger>
          <TabsTrigger value="details">Invoice Details</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Aging Bucket Bar Chart */}
            <Card data-testid="aging-bucket-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Aging Buckets by Amount</CardTitle>
                <CardDescription className="text-xs">Outstanding amounts grouped by days overdue</CardDescription>
              </CardHeader>
              <CardContent>
                {buckets.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={buckets}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${cs}${fmt(v)}`} />
                      <Tooltip
                        formatter={(v) => [`${cs}${v?.toLocaleString()}`, 'Amount']}
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                        {buckets.map((_, i) => (
                          <Cell key={i} fill={BUCKET_COLORS[i]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No aging data</p>
                )}
              </CardContent>
            </Card>

            {/* Aging Distribution Pie */}
            <Card data-testid="aging-pie-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Aging Distribution</CardTitle>
                <CardDescription className="text-xs">Proportion of outstanding by aging bucket</CardDescription>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} dataKey="value"
                          label={({ x, y, percent }) => percent > 0.05 ? (
                            <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
                              {`${(percent * 100).toFixed(0)}%`}
                            </text>
                          ) : null} labelLine={false}>
                          {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                        </Pie>
                        <Tooltip formatter={v => [`${cs}${v?.toLocaleString()}`, 'Amount']} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-3 justify-center mt-2">
                      {buckets.map((b, i) => (
                        <div key={b.bucket} className="flex items-center gap-1.5 text-xs">
                          <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: BUCKET_COLORS[i] }} />
                          <span className="text-muted-foreground">{b.bucket}: {b.count} inv</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No aging data</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Aging by Category */}
          {catAging.length > 0 && (
            <Card data-testid="category-aging-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Overdue by Category</CardTitle>
                <CardDescription className="text-xs">Categories with the highest overdue amounts</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={catAging} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${cs}${fmt(v)}`} />
                    <YAxis type="category" dataKey="category" width={120} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={v => [`${cs}${v?.toLocaleString()}`, 'Overdue']} />
                    <Bar dataKey="overdue_amount" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Overdue Suppliers Tab */}
        <TabsContent value="suppliers">
          <Card data-testid="overdue-suppliers-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Overdue Suppliers</CardTitle>
              <CardDescription className="text-xs">{overdue.length} supplier(s) with overdue invoices</CardDescription>
            </CardHeader>
            <CardContent>
              {overdue.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="suppliers-aging-table">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">Supplier</th>
                        <th className="text-right py-2 px-3 font-medium">Overdue Amount</th>
                        <th className="text-right py-2 px-3 font-medium">Outstanding</th>
                        <th className="text-center py-2 px-3 font-medium">Invoices</th>
                        <th className="text-center py-2 px-3 font-medium">Oldest Overdue</th>
                        <th className="text-left py-2 px-3 font-medium">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overdue.map((s, i) => (
                        <tr key={i} className="border-b hover:bg-muted/50" data-testid={`supplier-row-${i}`}>
                          <td className="py-2 px-3 font-medium">{s.supplier}</td>
                          <td className="py-2 px-3 text-right font-mono text-red-500">{cs}{s.overdue_amount?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right font-mono">{cs}{s.total_outstanding?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-center">{s.invoice_count}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge variant={s.oldest_overdue_days > 90 ? 'destructive' : s.oldest_overdue_days > 60 ? 'default' : 'secondary'} className="text-[10px]">
                              {s.oldest_overdue_days}d
                            </Badge>
                          </td>
                          <td className="py-2 px-3">{s.category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No overdue suppliers found</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Details Tab */}
        <TabsContent value="details">
          <Card data-testid="invoice-details-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Invoice Details</CardTitle>
              <CardDescription className="text-xs">{sortedDetails.length} outstanding invoice(s)</CardDescription>
            </CardHeader>
            <CardContent>
              {sortedDetails.length > 0 ? (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="w-full text-xs" data-testid="details-table">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b">
                        <SortHeader field="supplier_name">Supplier</SortHeader>
                        <SortHeader field="invoice_number">Invoice #</SortHeader>
                        <SortHeader field="invoice_date">Invoice Date</SortHeader>
                        <SortHeader field="due_date">Due Date</SortHeader>
                        <SortHeader field="amount">Amount</SortHeader>
                        <SortHeader field="days_overdue">Days Overdue</SortHeader>
                        <th className="text-left py-2 px-3 font-medium">Bucket</th>
                        <th className="text-left py-2 px-3 font-medium">Terms</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDetails.map((d, i) => (
                        <tr key={d.id || i} className="border-b hover:bg-muted/50" data-testid={`detail-row-${i}`}>
                          <td className="py-1.5 px-3 font-medium">{d.supplier_name}</td>
                          <td className="py-1.5 px-3 font-mono">{d.invoice_number || '-'}</td>
                          <td className="py-1.5 px-3">{d.invoice_date}</td>
                          <td className="py-1.5 px-3">{d.due_date}</td>
                          <td className="py-1.5 px-3 text-right font-mono">{cs}{d.amount?.toLocaleString()}</td>
                          <td className="py-1.5 px-3 text-center">
                            {d.days_overdue > 0 ? (
                              <Badge variant={d.days_overdue > 90 ? 'destructive' : d.days_overdue > 60 ? 'default' : 'secondary'} className="text-[10px]">
                                {d.days_overdue}d
                              </Badge>
                            ) : (
                              <span className="text-emerald-600 font-medium">Current</span>
                            )}
                          </td>
                          <td className="py-1.5 px-3">
                            <Badge variant="outline" className="text-[10px]">{d.bucket}</Badge>
                          </td>
                          <td className="py-1.5 px-3 text-muted-foreground">{d.payment_terms}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No outstanding invoices</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <AIPanel page="invoice-aging" filters={filters} />
    </div>
  );
}
